import React, {useEffect, useMemo, useState} from "react"
import {useParams, useSearchParams} from "react-router-dom"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Stack,
    TextField,
    Typography
} from "@mui/material"
import {LoadingButton} from "@mui/lab"
import {ArrowDownward} from "@mui/icons-material"

import SimpleTable from "../../components/Common/Elements"
import {accountInfoExtractors} from "../UserStates/CopyState/accountInfoExtractors"

import {
    useGetStateV2CMQuery,
    useCreateStateV2CMMutation,
    useAdvancedSearchCMMutation
} from "../../core/api/cloclmaker-state-api"
import {
    useGetStateV2DSQuery,
    useCreateStateV2DSMutation,
    useAdvancedSearchDSMutation
} from "../../core/api/ds-state-api"
import {
    useGetStateV2TOHQuery,
    useCreateStateV2TOHMutation,
    useAdvancedSearchTOHMutation
} from "../../core/api/toh-state-api"
import {
    useGetStateV2MCQuery,
    useCreateStateV2MCMutation,
    useAdvancedSearchMCMutation
} from "../../core/api/mc-state-api"

// Compact progress-transfer page designed to be embedded by the Zendesk app
// (zendesk-transfer/) into the ticket sidebar (~320px wide, everything is
// stacked vertically). The Zendesk app passes ticket data via query params:
//   newUserId / newVendorId - target account resolved from the ticket fields
//   facebookId / globalId / reserveId - ids to auto-search the old account
//   ticketId - Zendesk ticket id, stored in the copy log
//   tags - comma-separated ticket tags (for the paying/newbie check)
export const TRANSFER_SUPPORTED_GAMES = ["cm", "ds", "toh", "mc"]

const decodeStateValue = (base64) =>
    new TextDecoder().decode(Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)))

const parseStateData = (obj) => {
    if (!obj || typeof obj !== "object" || !obj.hasOwnProperty("data")) return null
    try {
        return typeof obj.data === "string" ? JSON.parse(obj.data) : obj.data
    } catch (e) {
        return null
    }
}

const extractRows = (game, stateObj) => {
    const data = parseStateData(stateObj)
    if (!data) return null
    try {
        return accountInfoExtractors[game](data)
    } catch (e) {
        return null
    }
}

const AccountCard = ({title, id, vendorId, query}) => (
    <Card variant="outlined">
        <CardContent sx={{pb: 0}}>
            <Typography sx={{fontSize: 14, fontWeight: "bold"}}>{title}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{wordBreak: "break-all"}}>
                {id}{vendorId && vendorId !== id ? ` / ${vendorId}` : ""}
            </Typography>
        </CardContent>
        {(query.isLoading || query.isFetching) && (
            <Box sx={{display: "flex", justifyContent: "center", p: 2}}>
                <CircularProgress size={24}/>
            </Box>
        )}
        {query.isError && (
            <CardContent>
                <Alert severity="error">Стейт не найден</Alert>
            </CardContent>
        )}
        {query.isSuccess && <SimpleTable data={query.rows || []}/>}
        {query.isSuccess && !query.rows && (
            <CardContent>
                <Alert severity="warning">Не удалось разобрать стейт</Alert>
            </CardContent>
        )}
    </Card>
)

const ZendeskTransfer = () => {
    const {game} = useParams()
    const [searchParams] = useSearchParams()

    const ticketId = searchParams.get("ticketId") || ""
    const tags = (searchParams.get("tags") || "").split(",").filter(Boolean)
    const searchIds = {
        facebookId: searchParams.get("facebookId") || "",
        globalUserID: searchParams.get("globalId") || "",
        reserveId: searchParams.get("reserveId") || ""
    }

    const [newUserId, setNewUserId] = useState(searchParams.get("newUserId") || "")
    const [newVendorId, setNewVendorId] = useState(searchParams.get("newVendorId") || "")
    const [oldUserId, setOldUserId] = useState("")
    const [oldVendorId, setOldVendorId] = useState("")
    const [submitted, setSubmitted] = useState(null)
    const [checkedSamePlayer, setCheckedSamePlayer] = useState(false)
    const [checkedIdsCorrect, setCheckedIdsCorrect] = useState(false)
    const [transferResult, setTransferResult] = useState(null)

    const [searchCM, searchCMResponse] = useAdvancedSearchCMMutation()
    const [searchDS, searchDSResponse] = useAdvancedSearchDSMutation()
    const [searchTOH, searchTOHResponse] = useAdvancedSearchTOHMutation()
    const [searchMC, searchMCResponse] = useAdvancedSearchMCMutation()
    const search = {cm: searchCM, ds: searchDS, toh: searchTOH, mc: searchMC}[game]
    const searchResponse = {cm: searchCMResponse, ds: searchDSResponse, toh: searchTOHResponse, mc: searchMCResponse}[game] || {}

    const hasSearchIds = Object.values(searchIds).some(Boolean)

    useEffect(() => {
        if (search && hasSearchIds) {
            search(searchIds)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game])

    const candidates = useMemo(() => {
        if (!Array.isArray(searchResponse.data)) return null
        return searchResponse.data.map((item) => ({
            ...item,
            isNewAccount: item.stateID === newUserId || item.vendorID === newVendorId
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchResponse.data])

    // Mode A: exactly one candidate besides the new account itself - prefill.
    useEffect(() => {
        if (!candidates || oldUserId) return
        const others = candidates.filter((c) => !c.isNewAccount)
        if (others.length === 1) {
            setOldUserId(others[0].stateID || "")
            setOldVendorId(others[0].vendorID || "")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidates])

    const makeQueryArg = (userId, vendorId) => ({
        deviceId: userId,
        stateId: vendorId || userId,
        ...(game === "cm" ? {version: "v2"} : {})
    })

    const skipOld = !submitted || !submitted.oldUserId
    const skipNew = !submitted || !submitted.newUserId

    const oldCM = useGetStateV2CMQuery(makeQueryArg(submitted?.oldUserId, submitted?.oldVendorId), {skip: skipOld || game !== "cm"})
    const oldDS = useGetStateV2DSQuery(makeQueryArg(submitted?.oldUserId, submitted?.oldVendorId), {skip: skipOld || game !== "ds"})
    const oldTOH = useGetStateV2TOHQuery(makeQueryArg(submitted?.oldUserId, submitted?.oldVendorId), {skip: skipOld || game !== "toh"})
    const oldMC = useGetStateV2MCQuery(makeQueryArg(submitted?.oldUserId, submitted?.oldVendorId), {skip: skipOld || game !== "mc"})

    const newCM = useGetStateV2CMQuery(makeQueryArg(submitted?.newUserId, submitted?.newVendorId), {skip: skipNew || game !== "cm"})
    const newDS = useGetStateV2DSQuery(makeQueryArg(submitted?.newUserId, submitted?.newVendorId), {skip: skipNew || game !== "ds"})
    const newTOH = useGetStateV2TOHQuery(makeQueryArg(submitted?.newUserId, submitted?.newVendorId), {skip: skipNew || game !== "toh"})
    const newMC = useGetStateV2MCQuery(makeQueryArg(submitted?.newUserId, submitted?.newVendorId), {skip: skipNew || game !== "mc"})

    const oldQuery = {cm: oldCM, ds: oldDS, toh: oldTOH, mc: oldMC}[game] || {}
    const newQuery = {cm: newCM, ds: newDS, toh: newTOH, mc: newMC}[game] || {}

    const [createCM, createCMResponse] = useCreateStateV2CMMutation()
    const [createDS, createDSResponse] = useCreateStateV2DSMutation()
    const [createTOH, createTOHResponse] = useCreateStateV2TOHMutation()
    const [createMC, createMCResponse] = useCreateStateV2MCMutation()
    const create = {cm: createCM, ds: createDS, toh: createTOH, mc: createMC}[game]
    const createResponse = {cm: createCMResponse, ds: createDSResponse, toh: createTOHResponse, mc: createMCResponse}[game] || {}

    if (!TRANSFER_SUPPORTED_GAMES.includes(game)) {
        return <Alert severity="error" sx={{m: 1}}>Игра "{game}" не поддерживается для переноса прогресса</Alert>
    }

    const handleShow = () => {
        setTransferResult(null)
        setCheckedSamePlayer(false)
        setCheckedIdsCorrect(false)
        setSubmitted({
            oldUserId: oldUserId.trim(),
            oldVendorId: oldVendorId.trim(),
            newUserId: newUserId.trim(),
            newVendorId: newVendorId.trim()
        })
    }

    const handleChooseCandidate = (candidate) => {
        setOldUserId(candidate.stateID || "")
        setOldVendorId(candidate.vendorID || "")
        setSubmitted(null)
        setTransferResult(null)
    }

    const handleTransfer = async () => {
        let body
        try {
            body = decodeStateValue(oldQuery.data.value)
        } catch (e) {
            setTransferResult({error: "Не удалось прочитать стейт старого аккаунта"})
            return
        }

        const response = await create({
            userId: submitted.newUserId,
            vendorId: submitted.newVendorId || submitted.newUserId,
            game,
            body,
            version: "v2",
            isDirectCopy: true,
            sourceUserId: submitted.oldUserId,
            sourceVendorId: submitted.oldVendorId || submitted.oldUserId,
            comment: ticketId ? `Zendesk ticket #${ticketId}` : "Zendesk transfer",
            zendeskTicketId: ticketId || undefined
        })

        if (response.error) {
            const detail = response.error.data?.message || response.error.data || response.error.status
            setTransferResult({error: `Ошибка переноса: ${JSON.stringify(detail)}`})
        } else {
            setTransferResult({success: true})
        }
    }

    const oldRows = oldQuery.isSuccess ? extractRows(game, oldQuery.data) : null
    const newRows = newQuery.isSuccess ? extractRows(game, newQuery.data) : null

    const previousTransfers = (newQuery.data?.copyLogs || []).filter((log) => log.directCopy)
    const hasPlayerTag = tags.some((t) => t.includes("paying") || t.includes("newbie"))
    const bothLoaded = oldQuery.isSuccess && !!oldRows && newQuery.isSuccess
    const canTransfer = bothLoaded && checkedSamePlayer && checkedIdsCorrect && !transferResult?.success

    return (
        <Stack spacing={1.5} sx={{p: 1.5}}>
            <Typography variant="subtitle1">
                Перенос прогресса <Chip size="small" label={game}/>
                {ticketId && <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>тикет #{ticketId}</Typography>}
            </Typography>

            {tags.length > 0 && !hasPlayerTag && (
                <Alert severity="warning">
                    Тикет не содержит тегов платящего игрока или новичка. Продолжайте только если уверены.
                </Alert>
            )}

            <TextField
                label="Новый ID (из тикета)"
                size="small"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                fullWidth
            />
            <TextField
                label="Новый Vendor ID"
                size="small"
                value={newVendorId}
                onChange={(e) => setNewVendorId(e.target.value)}
                helperText="Опционально, по умолчанию равен новому ID"
                fullWidth
            />

            <Divider/>

            {hasSearchIds && (searchResponse.isLoading || searchResponse.status === "pending") && (
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <CircularProgress size={16}/>
                    <Typography variant="caption">Поиск старого аккаунта…</Typography>
                </Box>
            )}

            {candidates && candidates.length > 0 && (
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        Найденные аккаунты (поиск по ID из тикета):
                    </Typography>
                    {candidates.map((c, i) => (
                        <Card key={i} variant="outlined" sx={{p: 1}}>
                            <Stack direction="row" spacing={1} sx={{alignItems: "center", justifyContent: "space-between"}}>
                                <Box sx={{minWidth: 0}}>
                                    <Typography variant="body2" sx={{wordBreak: "break-all"}}>{c.stateID}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {c.date || ""}{c.isNewAccount ? " — текущий (новый) аккаунт" : ""}
                                    </Typography>
                                </Box>
                                {!c.isNewAccount && (
                                    <Button size="small" variant="outlined" onClick={() => handleChooseCandidate(c)}>
                                        Выбрать
                                    </Button>
                                )}
                            </Stack>
                        </Card>
                    ))}
                </Stack>
            )}

            {candidates && candidates.filter((c) => !c.isNewAccount).length === 0 && (
                <Alert severity="info">Старый аккаунт не найден автоматически — введите ID вручную.</Alert>
            )}

            <TextField
                label="Старый ID (откуда переносим)"
                size="small"
                value={oldUserId}
                onChange={(e) => setOldUserId(e.target.value)}
                fullWidth
            />
            <TextField
                label="Старый Vendor ID"
                size="small"
                value={oldVendorId}
                onChange={(e) => setOldVendorId(e.target.value)}
                helperText="Опционально, по умолчанию равен старому ID"
                fullWidth
            />

            <Button
                variant="contained"
                onClick={handleShow}
                disabled={!oldUserId.trim() || !newUserId.trim()}
            >
                Показать данные аккаунтов
            </Button>

            {submitted && (
                <>
                    {previousTransfers.length > 0 && (
                        <Alert severity="warning">
                            Перенос для этого игрока уже выполнялся: {previousTransfers[0].timestamp} из {previousTransfers[0].sourceUserId} ({previousTransfers[0].employeeEmail})
                        </Alert>
                    )}

                    <AccountCard
                        title="Старый аккаунт (источник)"
                        id={submitted.oldUserId}
                        vendorId={submitted.oldVendorId}
                        query={{...oldQuery, rows: oldRows}}
                    />
                    <Box sx={{display: "flex", justifyContent: "center"}}>
                        <ArrowDownward color="warning"/>
                    </Box>
                    <AccountCard
                        title="Новый аккаунт (будет перезаписан)"
                        id={submitted.newUserId}
                        vendorId={submitted.newVendorId}
                        query={{...newQuery, rows: newRows}}
                    />

                    {bothLoaded && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkedSamePlayer}
                                        onChange={(e) => setCheckedSamePlayer(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="body2">Проверил и убедился, что оба ID принадлежат одному игроку</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkedIdsCorrect}
                                        onChange={(e) => setCheckedIdsCorrect(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="body2">Проверил и убедился в правильности ввода ID</Typography>}
                            />
                            <LoadingButton
                                variant="contained"
                                color="warning"
                                loading={createResponse.isLoading}
                                disabled={!canTransfer}
                                onClick={handleTransfer}
                            >
                                Перенести прогресс
                            </LoadingButton>
                        </>
                    )}

                    {transferResult?.success && (
                        <Alert severity="success">
                            Перенос выполнен: {submitted.oldUserId} → {submitted.newUserId}. Операция записана в лог Hermes.
                        </Alert>
                    )}
                    {transferResult?.error && (
                        <Alert severity="error">{transferResult.error}</Alert>
                    )}
                </>
            )}
        </Stack>
    )
}

export default ZendeskTransfer
